import { useEffect, useRef, useState } from "react";
import { Loader2, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCachedGet } from "@/lib/cache";
import { useLang } from "@/context/LanguageContext";

export const useL = () => {
  const { lang } = useLang();
  return (ar, en) => (lang === "en" ? en : ar);
};

const LIVE_MS = 10000;

// Admin data is shared with the mobile app: refresh silently every 10s and on tab focus so changes made in the app appear live.
export function useAdminFetch(path, params) {
  const { data, loading, error, reload, setData } = useCachedGet(path, params);
  const reloadRef = useRef(reload);
  reloadRef.current = reload;
  useEffect(() => {
    const tick = () => { if (document.visibilityState === "visible") reloadRef.current?.(); };
    const id = setInterval(tick, LIVE_MS);
    window.addEventListener("focus", tick);
    return () => { clearInterval(id); window.removeEventListener("focus", tick); };
  }, []);
  return { data, loading, error, reload, setData };
}

export function AsyncButton({ onClick, icon: Icon, children, disabled, ...props }) {
  const [pending, setPending] = useState(false);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  const run = async (e) => {
    if (pending) return;
    setPending(true);
    try { await onClick(e); } finally { if (alive.current) setPending(false); }
  };
  return (
    <Button {...props} disabled={disabled || pending} onClick={run} aria-busy={pending}>
      {pending ? <Loader2 className="w-4 h-4 animate-spin" /> : Icon && <Icon className="w-4 h-4" />} {children}
    </Button>
  );
}

export function Chip({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-5 py-2.5 rounded-full text-sm font-semibold border transition-colors duration-200 ${active ? "bg-[#0F4D32] text-white border-[#0F4D32]" : "bg-white text-slate-800 border-slate-200 hover:border-[#1A5C3A]"}`}>
      {children}
    </button>
  );
}

export function SectionTitle({ children, action }) {
  return (
    <div className="flex items-center justify-between mb-5">
      <h2 className="font-headline text-lg font-bold text-slate-900">{children}</h2>
      {action}
    </div>
  );
}

export const Loading = () => <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#1A5C3A]" /></div>;

export function LoadError({ onRetry }) {
  const L = useL();
  return (
    <div className="text-center py-16" data-testid="admin-load-error">
      <p className="text-red-600 mb-3">{L("تعذر تحميل البيانات", "Could not load data")}</p>
      <button onClick={onRetry} className="px-5 py-2 rounded-full border border-slate-200 bg-white text-sm font-semibold" data-testid="admin-retry">{L("إعادة المحاولة", "Retry")}</button>
    </div>
  );
}

export const Empty = ({ children, testid = "admin-empty" }) => <p className="text-center text-slate-500 py-16" data-testid={testid}>{children}</p>;

export const Card = ({ children, className = "", ...rest }) => <div className={`bg-white rounded-2xl border border-slate-200/70 ${className}`} {...rest}>{children}</div>;

export const fmtDate = (v, lang) => (v ? new Date(v.endsWith?.("Z") || v.includes?.("+") ? v : `${v}Z`).toLocaleString(lang === "en" ? "en-GB" : "ar-AE-u-nu-latn", { dateStyle: "medium", timeStyle: "short" }) : "");

export function ComingSoon({ title }) {
  const L = useL();
  return (
    <Card className="p-10 text-center" data-testid="admin-coming-soon">
      <Clock className="w-10 h-10 mx-auto text-[#1A5C3A] mb-3" />
      <h3 className="font-headline font-bold text-lg">{title}</h3>
      <p className="text-slate-500 text-sm mt-1">{L("هذا القسم قريباً", "This section is coming soon")}</p>
    </Card>
  );
}
